import { memo, useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Checkbox, FieldSet, Label, LabeledControl, Legend, Toast, useToast } from '@zillow/constellation-10';

import { DatarouterError } from '@zg-rentals/rental-manager-env';
import {
  useRentalManagerUserPropertyEditWebApiHandler_hasFlexibleLeaseLengths,
  useRentalManagerUserPropertyEditWebApiHandler_hasLessThanOneYearLeaseLengths,
  useRentalManagerUserPropertyEditWebApiHandler_hasMonthlyLeaseLengths,
  useRentalManagerUserPropertyEditWebApiHandler_hasMoreThanOneYearLeaseLengths,
  useRentalManagerUserPropertyEditWebApiHandler_hasNoneLeaseLengths,
  useRentalManagerUserPropertyEditWebApiHandler_hasOneYearLeaseLengths,
  useRentalManagerUserPropertyEditWebApiHandler_hasSixMonthsLeaseLengths,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { useBatchMutationToast } from '../../../../hooks/useBatchMutationToast';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';
import { getIsInputFormDisabled } from '../../utils';

import { DEFAULT_ERROR_MESSAGE } from '../../../../constants/ErrorMessageConstants';
import {
  LEASE_LENGTH_METADATA,
  LEASE_LENGTH_TYPE_KEYS,
  type LeaseLengthKey,
} from '../../../../constants/KeyFeaturesConstants';

import { useAuth } from '../../../AuthProvider';

type LocalState = Record<LeaseLengthKey, boolean>;

const NONE_KEY: LeaseLengthKey = 'hasNone';

const LeaseLengthAmenities = (): JSX.Element | null => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const leaseLengths = listingDetails?.buildingAttributes?.leaseLengths;
  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const [localState, setLocalState] = useState<LocalState>({
    hasFlexible: leaseLengths?.hasFlexible ?? false,
    hasMonthly: leaseLengths?.hasMonthly ?? false,
    hasSixMonths: leaseLengths?.hasSixMonths ?? false,
    hasOneYear: leaseLengths?.hasOneYear ?? false,
    hasLessThanOneYear: leaseLengths?.hasLessThanOneYear ?? false,
    hasMoreThanOneYear: leaseLengths?.hasMoreThanOneYear ?? false,
    hasNone: leaseLengths?.hasNone ?? false,
  });

  const { startRequest, handleSuccess, handleError } = useBatchMutationToast({
    successMessage: 'Lease lengths updated',
  });

  const isInputFormDisabled = getIsInputFormDisabled(listingDetails, canUpdateListing);

  const { mutateAsync: updateFlexible } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasFlexibleLeaseLengths,
    skipToast: true,
    meta: { context: { api: 'hasFlexibleLeaseLengths', details: { listingAlias } } },
  });

  const { mutateAsync: updateMonthly } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasMonthlyLeaseLengths,
    skipToast: true,
    meta: { context: { api: 'hasMonthlyLeaseLengths', details: { listingAlias } } },
  });

  const { mutateAsync: updateSixMonths } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasSixMonthsLeaseLengths,
    skipToast: true,
    meta: { context: { api: 'hasSixMonthsLeaseLengths', details: { listingAlias } } },
  });

  const { mutateAsync: updateOneYear } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasOneYearLeaseLengths,
    skipToast: true,
    meta: { context: { api: 'hasOneYearLeaseLengths', details: { listingAlias } } },
  });

  const { mutateAsync: updateLessThanOneYear } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasLessThanOneYearLeaseLengths,
    skipToast: true,
    meta: { context: { api: 'hasLessThanOneYearLeaseLengths', details: { listingAlias } } },
  });

  const { mutateAsync: updateMoreThanOneYear } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasMoreThanOneYearLeaseLengths,
    skipToast: true,
    meta: { context: { api: 'hasMoreThanOneYearLeaseLengths', details: { listingAlias } } },
  });

  const { mutateAsync: updateNone } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasNoneLeaseLengths,
    skipToast: true,
    meta: { context: { api: 'hasNoneLeaseLengths', details: { listingAlias } } },
  });

  const mutationMap: Record<keyof LocalState, typeof updateFlexible> = {
    hasFlexible: updateFlexible,
    hasMonthly: updateMonthly,
    hasSixMonths: updateSixMonths,
    hasOneYear: updateOneYear,
    hasLessThanOneYear: updateLessThanOneYear,
    hasMoreThanOneYear: updateMoreThanOneYear,
    hasNone: updateNone,
  };

  const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = event.target.name as LeaseLengthKey;
    const checked = event.target.value === 'true';

    startRequest();

    // Special case: clicking "None" should deselect all others
    if (fieldName === NONE_KEY && checked) {
      const previousState = { ...localState };
      const newState = LEASE_LENGTH_TYPE_KEYS.reduce(
        (acc, key) => ({ ...acc, [key]: key === NONE_KEY }),
        {} as Record<LeaseLengthKey, boolean>,
      );
      setLocalState(newState);

      try {
        // Set "None" to true
        await mutationMap[NONE_KEY]({
          getParams: { [LEASE_LENGTH_METADATA[NONE_KEY].apiKey]: true, listingAlias },
        });

        // Set all others to false
        await Promise.all(
          LEASE_LENGTH_TYPE_KEYS.filter((k) => k !== NONE_KEY).map((key) =>
            mutationMap[key]({
              getParams: { [LEASE_LENGTH_METADATA[key].apiKey]: false, listingAlias },
            }),
          ),
        );

        handleSuccess();
      } catch (err) {
        handleError();
        setLocalState(previousState);
        const errorMessage = DatarouterError.getDisplayMessage(
          err,
          'Something went wrong, refresh the page and try again',
        );
        enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
      }
      return;
    }

    // Special case: clicking any other option should deselect "None"
    const previousValue = !checked;
    const previousNoneValue = localState.hasNone;
    setLocalState((prev) => ({
      ...prev,
      [fieldName]: checked,
      hasNone: false, // Always deselect "None" when selecting anything else
    }));

    try {
      const metadata = LEASE_LENGTH_METADATA[fieldName];
      // Update the clicked field
      await mutationMap[fieldName]({
        getParams: { [metadata.apiKey]: checked, listingAlias },
      });

      // If "None" was previously selected, also update it to false
      if (previousNoneValue) {
        await mutationMap[NONE_KEY]({
          getParams: { [LEASE_LENGTH_METADATA[NONE_KEY].apiKey]: false, listingAlias },
        });
      }

      handleSuccess();
    } catch (err) {
      handleError();
      setLocalState((prev) => ({ ...prev, [fieldName]: previousValue, hasNone: previousNoneValue }));
      const errorMessage = DatarouterError.getDisplayMessage(err, DEFAULT_ERROR_MESSAGE);
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  };

  return (
    <Box data-testid="LeaseLengthAmenities" css={{ display: 'flex', flexDirection: 'column', gap: '400' }}>
      <FieldSet legend={<Legend>Lease length</Legend>} disabled={isInputFormDisabled}>
        <Box css={{ display: 'flex', flexDirection: 'row', gap: '400', flexWrap: 'wrap' }}>
          <Box css={{ display: 'flex', flexDirection: 'column', flex: '1 0 280px' }}>
            {LEASE_LENGTH_TYPE_KEYS.slice(0, Math.floor((LEASE_LENGTH_TYPE_KEYS.length + 1) / 2)).map((fieldName) => {
              const metadata = LEASE_LENGTH_METADATA[fieldName];
              return (
                <LabeledControl
                  key={`leaseLengths-${metadata.label}`}
                  label={<Label>{metadata.label}</Label>}
                  control={
                    <Checkbox
                      name={fieldName}
                      value={String(!localState[fieldName])}
                      checked={localState[fieldName]}
                      onChange={handleCheckboxChange}
                    />
                  }
                />
              );
            })}
          </Box>

          <Box css={{ display: 'flex', flexDirection: 'column', flex: '1 0 280px' }}>
            {LEASE_LENGTH_TYPE_KEYS.slice(Math.floor((LEASE_LENGTH_TYPE_KEYS.length + 1) / 2)).map(
              (fieldName, index) => {
                const metadata = LEASE_LENGTH_METADATA[fieldName];
                return (
                  <LabeledControl
                    key={`leaseLengths-${Math.floor((LEASE_LENGTH_TYPE_KEYS.length + 1) / 2) + index}`}
                    label={<Label>{metadata.label}</Label>}
                    control={
                      <Checkbox
                        name={fieldName}
                        value={String(!localState[fieldName])}
                        checked={localState[fieldName]}
                        onChange={handleCheckboxChange}
                      />
                    }
                  />
                );
              },
            )}
          </Box>
        </Box>
      </FieldSet>
    </Box>
  );
};

export default memo(LeaseLengthAmenities);
